import { useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { fetchRecurring, createRecurring, updateRecurring, deleteRecurring } from '@/lib/api'
import type { RecurringTransaction, RecurringUpdateData } from '@/lib/api'
import { mutationErrorHandler, filterByScope } from '@/lib/utils'
import type { ScopeFilter } from '@/lib/utils'
import { useScope } from '@/lib/scope-context'

export type { RecurringTransaction } from '@/lib/api'

export function useRecurring(scopeOverride?: ScopeFilter) {
  const { scope } = useScope()
  const effectiveScope = scopeOverride ?? scope

  const { data: allRecurring = [], ...rest } = useQuery<RecurringTransaction[]>({
    queryKey: ['recurring', 'all'],
    queryFn: () => fetchRecurring(),
  })

  const data = useMemo(
    () => filterByScope(allRecurring, effectiveScope),
    [allRecurring, effectiveScope],
  )

  return { data, ...rest }
}

export function useCreateRecurring() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createRecurring,
    onSuccess: () => {
      toast.success('固定支出を追加しました')
      queryClient.invalidateQueries({ queryKey: ['recurring'] })
    },
    onError: mutationErrorHandler('保存に失敗しました'),
  })
}

export function useUpdateRecurring() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: RecurringUpdateData }) =>
      updateRecurring(id, data),
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey: ['recurring'] })
      const previous = queryClient.getQueryData<RecurringTransaction[]>(['recurring'])
      queryClient.setQueryData<RecurringTransaction[]>(['recurring'], (old) =>
        old?.map((r) => (r.id === id ? ({ ...r, ...data } as RecurringTransaction) : r))
      )
      return { previous }
    },
    onError: (error, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['recurring'], context.previous)
      }
      mutationErrorHandler('更新に失敗しました')(error)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring'] })
    },
  })
}

export function useDeleteRecurring() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteRecurring,
    onSuccess: () => {
      toast.success('固定支出を削除しました')
      queryClient.invalidateQueries({ queryKey: ['recurring'] })
    },
    onError: mutationErrorHandler('削除に失敗しました'),
  })
}
